import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { z } from "zod";

import {
  BaseForm,
  FormColorPicker,
  FormInput,
  FormToggle,
} from "components/BaseForm";
import { getRandomColor } from "helpers/color";
import { useMetaLeadsEventMutations } from "hooks/useMetaLeadsEventsTable";

const schema = z
  .object({
    name: z.string().min(1, "Name is required"),
    color: z.string().nullable().optional(),
    event_name: z.string().nullable().optional(),
    reports_to_meta: z.boolean(),
  })
  .refine(event => !event.reports_to_meta || !!event.event_name?.trim(), {
    message: "Event name is required when reporting to Meta",
    path: ["event_name"],
  });

const ConditionalEventName = () => {
  const reportsToMeta = useWatch({ name: "reports_to_meta" });
  if (!reportsToMeta) return null;
  return <FormInput name="event_name" label="Meta Event Name" required />;
};

const MetaLeadsEventForm = ({ event, onSuccess }) => {
  const { create, update } = useMetaLeadsEventMutations();

  const defaultValues = useMemo(
    () => ({
      name: event?.name ?? "",
      color: event?.color ?? getRandomColor(),
      event_name: event?.event_name ?? "",
      reports_to_meta: !!event?.reports_to_meta,
    }),
    [event]
  );

  const handleSubmit = async values => {
    const data = {
      name: values.name,
      color: values.color || null,
      event_name: values.event_name?.trim() || null,
      reports_to_meta: values.reports_to_meta,
    };
    if (event) {
      await update.mutateAsync({ id: event.id, data });
    } else {
      await create.mutateAsync(data);
    }
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onSuccess={onSuccess}
      submitButtonText="Save"
    >
      <FormInput name="name" label="Name" required />
      <FormColorPicker name="color" label="Color" />
      <FormToggle name="reports_to_meta" label="Reports to Meta" />
      <ConditionalEventName />
    </BaseForm>
  );
};

export default MetaLeadsEventForm;
